import React, { useState, useCallback, useEffect, useRef } from "react";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";

// Intervalo mínimo entre tentativas de refresh da foto para o mesmo contato
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

const useStyles = makeStyles((theme) => ({
  avatarWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.5)",
    borderRadius: "50%",
  },
  avatarFallback: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
  },
}));

/**
 * ContactAvatar - Avatar com refresh automático quando a imagem falha
 * 
 * Quando a imagem de perfil falha ao carregar:
 * 1. Exibe a letra inicial do contato
 * 2. Chama a API para forçar atualização da foto no backend
 * 3. O backend busca a nova foto no WhatsApp e envia via WebSocket
 * 4. O componente receberá a atualização via Redux/Context automaticamente
 * 
 * @param {Object} props
 * @param {Object} props.contact - Objeto do contato com id, name, number, profilePicUrl
 * @param {Object} props.style - Estilos adicionais para o Avatar
 * @param {string} props.className - Classes CSS adicionais
 * @param {Object} props.avatarProps - Props adicionais para passar ao Avatar
 */
const ContactAvatar = ({ 
  contact, 
  style, 
  className, 
  ...avatarProps 
}) => {
  const classes = useStyles();
  const [imageError, setImageError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Cooldown em vez de "1 tentativa por sessão": se o backend respondeu
  // "skipped" (orçamento/backoff), a URL não muda e nenhum WebSocket chega —
  // com o boolean antigo o avatar ficava mudo até recarregar a página.
  const lastRefreshAtRef = useRef(0);
  const retryTimerRef = useRef(null);
  const currentUrlRef = useRef(contact?.profilePicUrl);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup: cancela requisições e timers pendentes quando o componente é desmontado
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Cancela qualquer requisição em andamento
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // Reseta o estado quando a URL muda (ex: via WebSocket)
  useEffect(() => {
    if (contact?.profilePicUrl !== currentUrlRef.current) {
      currentUrlRef.current = contact?.profilePicUrl;
      setImageError(false);
      lastRefreshAtRef.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    }
  }, [contact?.profilePicUrl]);

  // Verifica se a URL é válida (não vazia, não nopicture, não placeholder)
  const isValidImageUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    if (url.includes('nopicture')) return false;
    if (url === 'x') return false; // URL inválida de teste
    if (url.length < 10) return false; // URLs muito curtas são inválidas
    return true;
  }, []);

  // Obtém a letra inicial para o fallback
  const getInitial = useCallback(() => {
    if (contact?.name && contact.name.length > 0) {
      return contact.name[0].toUpperCase();
    }
    if (contact?.number && contact.number.length > 0) {
      return contact.number[0];
    }
    return "?";
  }, [contact?.name, contact?.number]);

  // Função para solicitar refresh da foto ao backend
  const refreshProfilePic = useCallback(async () => {
    // Evita marteladas: no máximo 1 tentativa por cooldown para o mesmo contato
    if (
      !contact?.id ||
      isRefreshing ||
      Date.now() - lastRefreshAtRef.current < REFRESH_COOLDOWN_MS
    ) {
      return;
    }

    // Verifica se o componente ainda está montado
    if (!isMountedRef.current) {
      return;
    }

    lastRefreshAtRef.current = Date.now();
    setIsRefreshing(true);
    
    // Cria um novo AbortController para esta requisição
    abortControllerRef.current = new AbortController();
    
    try {
      // Chama a API para forçar atualização da foto com signal para cancelamento
      await api.post(`/contacts/${contact.id}/refresh-profile-pic`, null, {
        signal: abortControllerRef.current.signal
      });
      
      // O backend emitirá um WebSocket event que atualizará o contact no Redux/Context
      // A nova URL chegará via props e o useEffect acima vai resetar o estado
      
    } catch (error) {
      // Ignora erros de cancelamento (componente desmontado ou filtro alterado)
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      
      // Erros esperados que não devem ser logados (sem WhatsApp configurado, etc)
      const isExpectedError = error?.response?.data?.error?.includes('ERR_NO_DEF_WAPP_FOUND') ||
                              error?.response?.status === 400;
      
      if (isMountedRef.current && !isExpectedError && process.env.NODE_ENV === 'development') {
        console.warn(`[ContactAvatar] Failed to refresh profile pic:`, error?.response?.data || error.message);
      }
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
      abortControllerRef.current = null;
    }
  }, [contact?.id, contact?.name, isRefreshing]);

  // Handler para erro de carregamento de imagem
  const handleImageError = useCallback(() => {
    if (!isMountedRef.current) return;

    setImageError(true);

    // Solicita atualização (a própria função aplica o cooldown)
    refreshProfilePic();

    // Após o erro o <img> some do DOM e onError nunca mais dispara; agenda uma
    // nova exibição da imagem passado o cooldown — se ela carregar, ótimo; se
    // falhar de novo, este handler roda outra vez e o ciclo continua.
    if (!retryTimerRef.current) {
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (isMountedRef.current) {
          setImageError(false);
        }
      }, REFRESH_COOLDOWN_MS);
    }
  }, [refreshProfilePic]);

  // Determina se deve mostrar a imagem ou o fallback
  const showImage = isValidImageUrl(contact?.profilePicUrl) && !imageError;

  // Se não tem contato, mostra avatar padrão
  if (!contact) {
    return (
      <Avatar 
        style={style} 
        className={className}
        {...avatarProps}
      />
    );
  }

  return (
    <div className={classes.avatarWrapper}>
      <Avatar
        src={showImage ? contact.profilePicUrl : undefined}
        style={style}
        className={`${className || ''} ${!showImage ? classes.avatarFallback : ''}`}
        onError={handleImageError}
        imgProps={{
          loading: "lazy",
        }}
        {...avatarProps}
      >
        {/* Fallback: mostra a letra inicial quando não há imagem */}
        {!showImage && getInitial()}
      </Avatar>
      
      {/* Indicador de loading durante o refresh */}
      {isRefreshing && (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={20} />
        </div>
      )}
    </div>
  );
};

export default ContactAvatar;
